import PropTypes from "prop-types";
import { useState } from "react";
import { Button, Col, Row, Typography } from "antd";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import ActivityNotesCard from "./detail/ActivityNotesCard";
import DetailHeaderCard from "./detail/DetailHeaderCard";
import DetailViewV1 from "./DetailViewV1";
import { getTabContent } from "./detail/tabContentRegistry";

const { Text } = Typography;

/*
 * DetailViewPage — full detail screen layout.
 *
 * Pass only `moduleName` (and optionally `id`). When `id` is omitted the
 * component resolves it from the URL: params.id → ?id= → location.state.record.
 *
 * Tab switching and tab content are driven by the header config (jobConfig,
 * candidateConfig, …) + tabContentRegistry. The 'details' tab renders the
 * default DetailViewV1 + ActivityNotesCard layout. Every other tab is looked
 * up in tabContentRegistry and rendered in place of that layout.
 *
 * Route-based actions (popup overlays, page navigations) are handled directly
 * inside DetailHeaderCard using its own useNavigate / useLocation. This keeps
 * the click → navigate path as short as possible.
 *
 * onHeaderAction is only called for actions that have NO route configured
 * in the module config (custom parent-level handling).
 */
export default function DetailViewPage({
  moduleName,
  id: idProp,
  backPath = "",
  users = [],
  showActivity = true,
  showNotes = true,
  onHeaderAction,
  onHeaderTabChange,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const id =
    idProp ||
    params.id ||
    searchParams.get("jobId") ||
    searchParams.get("id") ||
    location.state?.record?._id ||
    location.state?.record?.id;

  // The active tab is driven by the `?tab=` URL param so a tab can be deep-linked
  // directly (e.g. /jobs/:id?tab=candidates&subTab=pipeline). Defaults to details.
  const activeTab = searchParams.get("tab") || "details";
  const [descRefreshKey, setDescRefreshKey] = useState(0);

  const handleTabChange = (key, meta) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
    onHeaderTabChange?.(key, meta);
  };

  const handleDescriptionSaved = () => setDescRefreshKey((k) => k + 1);

  // Only fires for actions without a route (routed actions are handled inside DetailHeaderCard).
  const handleHeaderAction = (key, payload) => {
    onHeaderAction?.(key, payload);
  };

  const TabContent = getTabContent(moduleName, activeTab);

  return (
    <div className="v1-page v1-detail-page">
      <div className="v1-page-inner">
        <DetailHeaderCard
          moduleName={moduleName}
          id={id}
          defaultTab={activeTab}
          onAction={handleHeaderAction}
          onTabChange={handleTabChange}
        />

        {TabContent ? (
          <TabContent
            id={id}
            moduleName={moduleName}
            onDescriptionSaved={handleDescriptionSaved}
          />
        ) : (
          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} lg={16}>
              <DetailViewV1
                moduleName={moduleName}
                id={id}
                refreshKey={descRefreshKey}
                onDescriptionSaved={handleDescriptionSaved}
              />
            </Col>
            <Col xs={24} lg={8}>
              <ActivityNotesCard
                moduleName={moduleName}
                id={id}
                users={users}
                showActivity={showActivity}
                showNotes={showNotes}
              />
            </Col>
          </Row>
        )}
      </div>

      {backPath && (
        <div className="v1-footer">
          <Text type="secondary">
            {id ? `Record ID: ${id}` : "Detail view"}
          </Text>
          <Button onClick={() => navigate(backPath)}>Back</Button>
        </div>
      )}
    </div>
  );
}

DetailViewPage.propTypes = {
  moduleName: PropTypes.string,
  id: PropTypes.string,
  backPath: PropTypes.string,
  users: PropTypes.array,
  showActivity: PropTypes.bool,
  showNotes: PropTypes.bool,
  onHeaderAction: PropTypes.func,
  onHeaderTabChange: PropTypes.func,
};
